import {Component, HostBinding, OnInit, ViewChild} from "@angular/core";
import {ExtendOrMoveProjectDialogComponent} from "../dialogs/extend-or-move-project-dialog/extend-or-move-project-dialog.component";
import {CopyAssignmentsDialogComponent} from "../dialogs/copy-assignments-dialog/copy-assignments-dialog.component";
import {Employee} from "../../core/models/Employee";
import {
	AssignData,
	AssignEmployeeDialogComponent,
} from "../dialogs/assign-employee-dialog/assign-employee-dialog.component";
import {MatDialog} from "@angular/material/dialog";
import {CustomizingService} from "../../core/services/services/customizing.service";
import {AuthenticationService} from "../../core/services/services/authentication.service";
import {ApplicationUser} from "../../core/models/ApplicationUser";
import {CustomizingAbsence} from "../../core/models/Enums/CustomizingAbsence";
import {SkillDbDialogComponent} from "../dialogs/skill-db-dialog/skill-db-dialog.component";
import {EmployeeSkill} from "../../core/models/EmployeeSkill";
import {EmployeeService} from "../../core/services/services/employee.service";
import {FilesDialogComponent} from "../dialogs/files-dialog/files-dialog.component";
import {TranslateService, TranslatePipe} from "@ngx-translate/core";
import {firstValueFrom} from "rxjs";
import {SidenavControllerService} from "src/app/core/services/services/sidenav-controller.service";
import {AppearanceService} from "../../core/services/services/appearance.service";
import {AppearanceHelperZoom} from "../../core/services/services/appearance-service.d/appearance-helper-zoom";
import {
	AppearanceHelperDarkMode,
	DarkModeSetting,
} from "src/app/core/services/services/appearance-service.d/appearance-helper-dark-mode";
import {MepPopoverDirective} from "src/app/shared/mep-popover/mep-popover.directive";
import {Role} from "../../core/models/Role";
import {RequiresPermissionDirective} from "../permission/directives/requires-permission.directive";
import {MatTooltip} from "@angular/material/tooltip";
import {MatIcon} from "@angular/material/icon";
import {RouterLinkActive, RouterLink} from "@angular/router";
import {MatDivider} from "@angular/material/divider";
import {NewsNotificationComponent} from "../news-notification/news-notification.component";
@Component({
	selector: "app-sidenav",
	templateUrl: "./sidenav.component.html",
	styleUrls: ["./sidenav.component.scss"],
	host: {
		"[class.sidenav-big]": "sidenavStateExpanded",
		"[class.sidenav-small]": "!sidenavStateExpanded",
	},
	imports: [
		RequiresPermissionDirective,
		MatTooltip,
		MatIcon,
		RouterLinkActive,
		RouterLink,
		MatDivider,
		MepPopoverDirective,
		TranslatePipe,
		NewsNotificationComponent,
	],
})
export class SidenavComponent implements OnInit {
	@ViewChild("zoomPopupController") zoomPopupController: MepPopoverDirective;

	@HostBinding("class.sidenav-big") get sidenavBig() {
		return this.sidenavStateExpanded;
	}
	@HostBinding("class.sidenav-small") get sidenavSmall() {
		return !this.sidenavStateExpanded;
	}

	employeeExpanded = false;
	projectExpanded = false;
	sidenavStateExpanded: boolean;
	multiple: boolean = false;
	customizingEnabledEmployeeProposal = 0;
	actionPlanFunctionalityEnabled = false;
	applicationUser: ApplicationUser;
	showAbsenceLink = true;
	showProfileMap = false;
	showChatBot: boolean;
	isRoleInReadonly = false;
	isCustomerManager = false;
	isExternal: boolean = false;
	deactivateMepSkillEditBUs: string;
	deactivateSkillEdit: boolean;
	private isSkillDialogOpening = false;
	private customizingEnableProposalLoaded = false;
	protected readonly darkModeSetting = DarkModeSetting;
	protected readonly appearanceHelperZoomClass = AppearanceHelperZoom;

	constructor(
		private matDialog: MatDialog,
		private customizingService: CustomizingService,
		public loginService: AuthenticationService,
		private _employeeService: EmployeeService,
		private _translate: TranslateService,
		protected appearanceService: AppearanceService,
		protected appearanceHelperZoom: AppearanceHelperZoom,
		protected appearanceHelperDarkMode: AppearanceHelperDarkMode,
		sidenavControllerService: SidenavControllerService,
	) {
		// has to be set in constructor, to prevent the opening animation initially

		sidenavControllerService.sidenavStatusChange.subscribe((value) => {
			this.sidenavStateExpanded = value;
		});

		sidenavControllerService.setInitialState().then();
	}

	async ngOnInit(): Promise<void> {
		await firstValueFrom(this.loginService.getUserRoles()).then((res) => {
			if (res[0] === "MA_SKILL_EDIT" || res[0] === "MA_SKILL_AND_ASSIGNMENT_EDIT") {
				this.isRoleInReadonly = true;
			}
			if (res[0] === "CUSTOMER_MANAGER") {
				this.isCustomerManager = true;
			}
			this.isExternal = res.includes(Role.EXTERNAL);
		});
		this.customizingService
			.isActionPlanFunctionalityEnabled()
			.then((enabled) => (this.actionPlanFunctionalityEnabled = enabled));

		await this.loginService.getApplicationUser().then((applicationUser) => (this.applicationUser = applicationUser));

		this.deactivateMepSkillEditBUs = await this.customizingService.getDeactivateMepSkillEdit();

		if (this.deactivateMepSkillEditBUs.length > 0 && this.isRoleInReadonly) {
			let businessUnits = this.deactivateMepSkillEditBUs.split(";");
			for (const bu of businessUnits) {
				if (bu.match(this.applicationUser.employee?.businessUnit)) {
					this.deactivateSkillEdit = true;
					break;
				}
			}
		}

		this.showChatBot = await this.customizingService.getChatBotCustomizing(this.applicationUser);

		this.customizingService.getCustomizingTable().then((customizingTable) => {
			this.showProfileMap = customizingTable.profileMapIFrame
				.split(";")
				.includes(this.applicationUser.employee?.businessUnit);
		});

		this.customizingService.getAbsenceSettings().then((absenceSetting) => {
			this.showAbsenceLink = absenceSetting !== CustomizingAbsence.DISABLE_ABSENCES;
		});

		this.registerShowingZoomInfoBlockOnce();
	}

	toggleEmployeeMenu($event: MouseEvent): boolean {
		this.employeeExpanded = !this.employeeExpanded;
		$event.stopPropagation();
		return false;
	}

	toggleProjectMenu($event: MouseEvent): boolean {
		this.projectExpanded = !this.projectExpanded;
		$event.stopPropagation();
		return false;
	}

	openChangeProjectEndDialog(): void {
		this.matDialog.open(ExtendOrMoveProjectDialogComponent, {
			disableClose: true,
			width: "1000px",
		});
	}

	openCopyAssignmentsDialog() {
		this.matDialog.open(CopyAssignmentsDialogComponent, {
			disableClose: true,
			width: "1000px",
		});
	}

	openEditMySkills() {
		if (!this.isSkillDialogOpening) {
			this.isSkillDialogOpening = true;
			this.loginService.getApplicationUser().then((user: ApplicationUser) => {
				let currentUser = user.employee;
				if (currentUser != null) {
					this._employeeService.getEmployeeSkillsById(currentUser.id).subscribe((currentUserSkills: EmployeeSkill[]) =>
						SkillDbDialogComponent.openEditSkillsDialog(
							this.matDialog,
							false,
							() => {
								this.isSkillDialogOpening = false;
							},
							currentUserSkills,
							currentUser,
						),
					);
				}
			});
		}
	}

	openMyProfileFiles() {
		this.loginService.getApplicationUser().then((user: ApplicationUser) => {
			let currentUser = user.employee;
			if (currentUser !== null) {
				this.matDialog.open(FilesDialogComponent, {
					data: {employee: currentUser},
					disableClose: true,
				});
			}
		});
	}

	async openAssignEmployeeDialog(employee: Employee = undefined): Promise<void> {
		if (!this.customizingEnableProposalLoaded) {
			this.customizingEnabledEmployeeProposal = parseInt(
				await this.customizingService.enabledProjectEmployeeProposal(),
			);
		}

		this.matDialog.open<AssignEmployeeDialogComponent, AssignData>(AssignEmployeeDialogComponent, {
			disableClose: true,
			width: "1200px",
			data: {
				employee: employee,
				disableProjects: false,
				enabledProposal: this.customizingEnabledEmployeeProposal,
			},
		});
	}

	private registerShowingZoomInfoBlockOnce() {
		this.appearanceHelperZoom.onZoomInfoShow.subscribe(() => {
			this.zoomPopupController.open();
		});
	}

	protected readonly role = Role;
}
